import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import { DeleteConfirmModal } from 'components/FormModal';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
  HeaderWrapper,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGuestSearch, withGuestSearchContext } from 'pages/track/Guests';
import { useDeleteGuest } from 'pages/track/Guests/gql';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import { formatEventDate, formatPhoneNumber } from 'shared/formatters';
import { useGetEventAndEventGuests } from '../gql/useGetEventAndEventGuests';
import GuestFormModal from '../GuestListEvents/components/GuestFormModal';
import { Logo } from './Desktop/logo';
import TrackEventGuestListSkeleton from './TrackEventGuestListSkeleton';

const TrackLogo = styled.img`
  width: 80px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
`;

const FilterPills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const TrackEventGuestListContent = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { guestSearchQuery: queryString } = useGuestSearch();

  const [activeTab, setActiveTab] = useState('event'); // 'event' or 'yearly'
  const [guestTypeToAdd, setGuestTypeToAdd] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  // Check for success/error messages from other pages (e.g., after guest deletion)
  useEffect(() => {
    const successMessage = sessionStorage.getItem('guestSuccessMessage');
    const errorMessage = sessionStorage.getItem('guestErrorMessage');

    if (successMessage) {
      setToasterData({
        title: 'Success',
        message: successMessage,
        type: 'success',
      });
      sessionStorage.removeItem('guestSuccessMessage');
    } else if (errorMessage) {
      setToasterData({
        title: 'Error',
        message: errorMessage,
        type: 'error',
      });
      sessionStorage.removeItem('guestErrorMessage');
    }
  }, []);

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch event and guests data
  const { data, loading, refetch } = useGetEventAndEventGuests({ queryString });

  const event = data?.getEvent;
  const guests = data?.getEventGuests;
  const eventGuests = guests?.event_guests || [];
  const yearlyGuests = guests?.yearly_guests || [];

  // Get current tab's guests
  const currentGuests = activeTab === 'event' ? eventGuests : yearlyGuests;
  const totalCount = eventGuests.length + yearlyGuests.length;

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(currentGuests.length / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle add guest
  const handleAddGuest = (type) => {
    if (!capabilities || capabilities?.add_guests) {
      setGuestTypeToAdd(type);
    } else {
      setShowPermissionModal(true);
    }
  };

  // Delete guest
  const deleteGuest = useDeleteGuest();

  const handleDeleteClick = (guest) => {
    setGuestToDelete(guest);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!guestToDelete) return;
    setIsDeleting(true);
    try {
      await deleteGuest({
        variables: { id: guestToDelete.id },
        refetchQueries: ['GetEventAndEventGuests'],
      });
      setToasterData({
        title: 'Guest Deleted',
        message: `${guestToDelete.first_name} ${guestToDelete.last_name} has been removed.`,
        type: 'success',
      });
    } catch (error) {
      setToasterData({
        title: 'Error',
        message: 'Failed to delete guest. Please try again.',
        type: 'error',
      });
    }
    setIsDeleting(false);
    setShowDeleteModal(false);
    setGuestToDelete(null);
  };

  // CSV Export helper
  const downloadFile = useCallback((filename, content, type = 'text/csv') => {
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    window.URL.revokeObjectURL(url);
  }, []);

  // Generate CSV
  const generateCSV = useCallback(() => {
    const renderGuests = (guestList, guestType) => {
      return guestList
        .map((guest) => {
          const { last_name, first_name, phone_number, additional_guests } =
            guest;
          return `"${last_name}, ${first_name}","${formatPhoneNumber(
            phone_number
          )}","${additional_guests}","${guestType}"`;
        })
        .join('\n');
    };

    const csv = `${
      event?.start_date ? moment(event.start_date).format('MMM DD') : ''
    } ${
      event?.end_date
        ? `- ${moment(event.end_date).format('MMM DD, YYYY')}`
        : ''
    } - ${event?.name || ''},,
Name, Phone Number, Additional Guests, Guest Type
${renderGuests(eventGuests, 'Single Event')}
${renderGuests(yearlyGuests, 'Yearly')}
`;

    return csv.trim();
  }, [event, eventGuests, yearlyGuests]);

  // Generate PDF
  const generatePDF = useCallback(() => {
    const unit = 'pt';
    const size = 'A4';
    const orientation = 'landscape';

    const doc = new jsPDF(orientation, unit, size, true);

    doc.setFontSize(15);

    const date = event?.start_date;
    const title = date
      ? moment(date, 'MM-DD-YYYY').format('ddd MMM DD - YYYY')
      : '';

    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

    const text = doc.splitTextToSize(title, pageWidth - 650, {});
    doc.text(text, 40, 40);
    doc.text(event?.name || '', 40, 60);

    const headers = [
      ['NAME', 'PHONE NUMBER', 'ADDITIONAL GUESTS', 'GUEST TYPE', 'DATE'],
    ];

    const eventGuestsData = eventGuests.map((guest) => {
      const {
        last_name,
        first_name,
        phone_number,
        additional_guests,
        created_at,
      } = guest;
      return [
        `${last_name}, ${first_name}`,
        formatPhoneNumber(phone_number),
        additional_guests,
        'Single Event',
        moment(created_at, 'MM-DD-YYYY')
          .format('MMM DD - YYYY h:mm A')
          .toUpperCase(),
      ];
    });

    const yearlyGuestsData = yearlyGuests.map((guest) => {
      const {
        last_name,
        first_name,
        phone_number,
        additional_guests,
        created_at,
      } = guest;
      return [
        `${last_name}, ${first_name}`,
        formatPhoneNumber(phone_number),
        additional_guests,
        'Yearly',
        moment(created_at, 'MM-DD-YYYY')
          .format('MMM DD - YYYY h:mm A')
          .toUpperCase(),
      ];
    });

    doc.text(`Event Guests (${eventGuests.length})`, 40, 80);
    doc.text('Guest List', 640, 70);
    doc.addImage(Logo, 'PNG', 760, 40, 50, 50, '', 'FAST');

    doc.autoTable({
      startY: 100,
      rowPageBreak: 'avoid',
      columns: [
        { dataKey: 'name', header: 'NAME' },
        { dataKey: 'phone_number', header: 'PHONE NUMBER' },
        { dataKey: 'additional_guests', header: 'ADDITIONAL GUESTS' },
        { dataKey: 'guest_type', header: 'GUEST TYPE' },
        { dataKey: 'created_at', header: 'DATE' },
      ],
      head: headers,
      body: [...eventGuestsData, ...yearlyGuestsData],
      styles: {
        fontStyle: 'bold',
      },
      headStyles: {
        fillColor: '#fa4616',
      },
      columnStyles: {
        name: { cellWidth: 200 },
        phone_number: { cellWidth: 100 },
        additional_guests: { cellWidth: 100 },
        guest_type: { cellWidth: 100 },
        created_at: { cellWidth: 100 },
      },
    });

    doc.setProperties({
      title: 'Guest List',
      subject: 'Guest List',
      author: 'Pit Pay',
    });

    return doc;
  }, [event, eventGuests, yearlyGuests]);

  // Handle export
  const handleExport = useCallback(
    (format) => {
      setShowExportModal(false);

      try {
        const eventName = event?.name || 'GuestList';
        const dateStr = moment().format('MMM-DD-YYYY');
        const filename = `${dateStr}-${eventName}-GuestList`;

        if (format === 'CSV') {
          const csvContent = generateCSV();
          downloadFile(`${filename}.csv`, csvContent, 'text/csv');
        } else if (format === 'PDF') {
          const doc = generatePDF();
          doc.save(`${filename}.pdf`);
        }

        setToasterData({
          title: 'Export Complete!',
          message: `${totalCount} guests exported to ${format}`,
          type: 'success',
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'An error occurred while exporting. Please try again.',
          type: 'error',
        });
      }
    },
    [event, totalCount, generateCSV, generatePDF, downloadFile]
  );

  // Paginate current guests
  const paginatedGuests = currentGuests.slice(
    (currentPage - 1) * 15,
    currentPage * 15
  );
  const totalPages = Math.ceil(currentGuests.length / 15);

  // DataTable columns
  const columns = [
    {
      field: 'name',
      header: 'Name',
      mobileTitle: true,
      render: (guest) => (
        <CellPrimary>
          {guest.last_name}, {guest.first_name}
        </CellPrimary>
      ),
    },
    {
      field: 'phone_number',
      header: 'Phone Number',
      render: (guest) => (
        <CellSecondary>{formatPhoneNumber(guest.phone_number)}</CellSecondary>
      ),
    },
    {
      field: 'additional_guests',
      header: 'Additional Guests',
      render: (guest) => guest.additional_guests || 0,
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Guest',
      mobileLabel: 'View',
      to: (guest) =>
        `${routePrefix}/guest-lists/event/${event?.id}/guest/${guest.id}`,
    },
    {
      type: 'delete',
      title: 'Delete Guest',
      mobileLabel: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  if (!event || !guests) {
    return <TrackEventGuestListSkeleton />;
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderWrapper>
            <HeaderContainer>
              <PageHeaderIcon icon="users" />
              <div>
                <HeaderTitle>{event.name}</HeaderTitle>
                <HeaderSubtitle>
                  {event.track?.name || event.series?.name || ''} -{' '}
                  {formatEventDate(event)}
                </HeaderSubtitle>
              </div>
            </HeaderContainer>
            {(event.track?.logo || event.series?.logo) && (
              <TrackLogo
                src={event.track?.logo || event.series?.logo}
                alt={event.track?.name || event.series?.name || 'Logo'}
              />
            )}
          </HeaderWrapper>
        </Card>

        {/* Dashboard Layout with Sidebar */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search and Add Button */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search guests..."
                    debounceMs={500}
                  />
                  <ButtonV2
                    onClick={() =>
                      handleAddGuest(
                        activeTab === 'event' ? 'event_guests' : 'yearly_guests'
                      )
                    }
                    style={{ width: 'auto', flexShrink: 0 }}
                  >
                    Add New Guest
                  </ButtonV2>
                </PageToolbar>

                {/* Filter Pills */}
                <FilterPills
                  key={`tabs-${eventGuests.length}-${yearlyGuests.length}`}
                >
                  <ButtonV2
                    variant="pill"
                    active={activeTab === 'event'}
                    onClick={() => {
                      setActiveTab('event');
                      updateQuery({ page: 1 });
                    }}
                  >
                    Single Event ({eventGuests.length})
                  </ButtonV2>
                  <ButtonV2
                    variant="pill"
                    active={activeTab === 'yearly'}
                    onClick={() => {
                      setActiveTab('yearly');
                      updateQuery({ page: 1 });
                    }}
                  >
                    Full Season ({yearlyGuests.length})
                  </ButtonV2>
                </FilterPills>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={paginatedGuests}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No guests found"
                  emptySubtext="Try adjusting your search or add a new guest"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Back to Events',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                      />
                    </svg>
                  ),
                  onClick: () => history.goBack(),
                },
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                  disabled: totalCount === 0,
                },
                {
                  label: 'View All Guests',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                      <path
                        fillRule="evenodd"
                        d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z"
                      />
                      <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                    </svg>
                  ),
                  onClick: () => history.push(`${routePrefix}/guests`),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Guest Form Modal */}
        <GuestFormModal
          isOpen={!!guestTypeToAdd}
          onClose={() => setGuestTypeToAdd(null)}
          queryString={queryString}
          onGuestCreated={() => {
            // Explicitly refetch to update tab counts
            refetch();
          }}
          onSuccess={(message) =>
            setToasterData({
              title: 'Success',
              message,
              type: 'success',
            })
          }
          onError={(message) =>
            setToasterData({
              title: 'Error',
              message,
              type: 'error',
            })
          }
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/1`}
          history={history}
        />

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={false}
          recordCount={totalCount}
          dataType="guests"
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setGuestToDelete(null);
          }}
          title="Delete Guest"
          message={`Are you sure you want to delete ${guestToDelete?.first_name} ${guestToDelete?.last_name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Toaster for notifications */}
        <Toaster
          isOpen={!!toasterData}
          title={toasterData?.title || ''}
          message={toasterData?.message}
          type={toasterData?.type}
          onClose={() => setToasterData(null)}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

const TrackEventGuestList = withGuestSearchContext(TrackEventGuestListContent);

export default TrackEventGuestList;
